<template>
  <!-- Remembered beneficiary type != the shared one · red, because the cancellation will submit a
       type contradicting what this form told the user — but never blocking, since the memory may
       simply be stale. Only `switchableType` (a type that would leave no conflict at all) makes
       "switch" honest advice; without one the note just states what will be submitted. Margin is
       left to the caller via class fallthrough, matching RegionMismatchAdvisory. -->
  <div class="error-note">
    <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
    <span>{{ message }}</span>
  </div>
</template>

<script setup lang="ts">
import type { BeneficiaryTypeConflict } from "@/dashboard/inventory_management/cancel/useCancelEditorAdvisories";
import type { BeneficiaryTypeEnum } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  /** Non-empty — the caller decides whether there is anything to say. */
  conflicts: BeneficiaryTypeConflict[];
  sharedType: BeneficiaryTypeEnum;
  /** The one offerable type that would leave no conflict at all, or null when none would. */
  switchableType: BeneficiaryTypeEnum | null;
}>();

const { t } = useI18n();

const message = computed(() => {
  const sharedType = beneficiaryTypeLabel(props.sharedType);
  if (props.switchableType) {
    return t("go_cancel.beneficiary_type_conflict_switch", {
      sharedType,
      names: props.conflicts.map((conflict) => conflict.name).join(", "),
      rememberedType: beneficiaryTypeLabel(props.switchableType),
    });
  }
  return t("go_cancel.beneficiary_type_conflict_mixed", {
    sharedType,
    details: props.conflicts
      .map(
        (conflict) =>
          `${conflict.name} (${beneficiaryTypeLabel(conflict.rememberedType)})`
      )
      .join(", "),
  });
});

const beneficiaryTypeLabel = (beneficiaryType: BeneficiaryTypeEnum): string =>
  t(`go_cancel.form.beneficiary_type.${beneficiaryType}`);
</script>
